import { useQuery } from '@apollo/client';
import classNames from 'classnames';
import { AnimatePresence } from 'framer-motion';
import * as _ from 'lodash-es';
import React, { useCallback, useEffect, useReducer, useState } from 'react';
import useLegacyIsLoggedIn from '../account/useIsLoggedIn';
import Button from '../components/Button';
import OpacityMotionTransition from '../components/OpacityMotionTransition';
import SlabLogo from '../components/SlabLogo';
import SVGIcon from '../components/SVGIcon';
import { AssetTokenProvider } from '../editor/AssetTokenContext';
import QuillComponent from '../editor/QuillComponent';
import { useQuill, withQuillContextProvider } from '../editor/QuillContext';
import useAnchorScrollingHandler from '../editor/useAnchorScrollingHandler';
import { gql } from '../graphql/generated/gql';
import tocIcon from '../icons/non-prefilled/16x16/tableOfContents.svg';
import PageMeta from '../utils/PageMeta';
import Link from '../utils/router/Link';
import useNavigate from '../utils/router/useNavigate';
import useSlug from '../utils/router/useSlug';
import postPath, { postEmbedPath } from '../utils/url/postPath';
import { IsPublicViewProvider } from '../utils/useIsPublicView';
import ConnectedTableOfContents from './actionButtons/ConnectedTableOfContents';
import TableOfContents from './actionButtons/TableOfContents';
import * as styles from './EmbeddedPostView.module.scss';
import PostSkeleton from './PostSkeleton';
import {
  AllowAccessPostViewError,
  NotLoggedInPostViewError,
  RequestAccessPostViewError,
} from './PostViewError';
import useEmbedAuthListener from './useEmbedAuthListener';
import useShareSecret from './useShareSecret';
import replacePostPath from './utils/replacePostPath';

const TOC_WIDTH = parseInt(styles.tocWidth, 10);
const TOC_TRANSITION_DURATION = parseInt(styles.tocTransitionDuration, 10);
const TOC_TRANSITION_TIMING_FUNCTION = styles.tocTransitionTimingFunction;

gql(`
  fragment EmbeddedPostView_post on PostFields {
    id
    title
    content(isLatest: true) {
      id
      delta
    }
    ...postPath_post
  }
`);

const POST_QUERY = gql(/** @complexity 3 */ `
  query embeddedPost($id: ID!, $shareSecret: String) {
    post(id: $id, view: true, shareSecret: $shareSecret) {
      ...EmbeddedPostView_post
    }
    entityAssetToken(entityId: $id, entityType: POST) {
      ...AssetTokenProvider_entityAssetToken
    }
  }
`);

const PUBLIC_POST_QUERY = gql(/** @complexity 3 */ `
  query publicPost($id: ID!, $shareSecret: String) {
    post: publicPost(id: $id, shareSecret: $shareSecret) {
      ...EmbeddedPostView_post
    }
    entityAssetToken(entityId: $id, entityType: POST) {
      ...AssetTokenProvider_entityAssetToken
    }
  }
`);

const EmbeddedNotFoundView = () => {
  const [postId] = useSlug('postSlug');
  const isInIframe = window.self !== window.top;
  const isLoggedIn = useLegacyIsLoggedIn();

  useEmbedAuthListener();

  if (isLoggedIn) {
    return <RequestAccessPostViewError postId={postId} />;
  }

  if (isInIframe) {
    return <AllowAccessPostViewError />;
  }

  return <NotLoggedInPostViewError postId={postId} />;
};

type State = { leftTransition: number; isOverlayMode: boolean };

const reducer = (
  _state: State,
  action:
    | { type: 'reset' }
    | { type: 'overlay' }
    | { type: 'offset'; transition: number },
): State => {
  switch (action.type) {
    case 'reset':
      return { leftTransition: 0, isOverlayMode: false };
    case 'overlay':
      return { leftTransition: 0, isOverlayMode: true };
    default:
      return { leftTransition: action.transition, isOverlayMode: false };
  }
};

const EmbeddedPostView = () => {
  const [state, dispatch] = useReducer(reducer, {
    leftTransition: 0,
    isOverlayMode: false,
  });
  const [postId, name] = useSlug('postSlug');
  const [isShowingTOC, originalSetIsShowingTOC] = useState(false);

  const isLoggedIn = useLegacyIsLoggedIn();
  const shareSecret = useShareSecret();

  const embeddedPostResult = useQuery(POST_QUERY, {
    variables: { id: postId, shareSecret },
    skip: !isLoggedIn,
  });

  const publicPostResult = useQuery(PUBLIC_POST_QUERY, {
    variables: { id: postId, shareSecret },
    skip: isLoggedIn,
  });

  const { data, error, loading } = isLoggedIn
    ? embeddedPostResult
    : publicPostResult;

  const post = data?.post;
  const entityAssetToken = data?.entityAssetToken;

  const { title, content } = post ?? {};
  const navigate = useNavigate();

  useEffect(() => {
    if (title) {
      replacePostPath(postEmbedPath({ id: postId, title }), navigate);
    }
  }, [navigate, postId, title]);

  useEffect(() => {
    if (!loading && post) {
      const { hash } = document.location;
      if (hash) {
        const header = document.getElementById(hash.slice(1));
        if (header != null) {
          header.scrollIntoView();
        }
      }
    }
  }, [loading, post]);

  const { quill } = useQuill();
  const anchorScrollingHandlerProps = useAnchorScrollingHandler(quill, postId);

  const calculateLeftOffset = useCallback(() => {
    const root = quill?.root;
    const postWidth = root?.getBoundingClientRect().width;
    const windowWidth = window.innerWidth;
    if (typeof postWidth !== 'number' || typeof windowWidth !== 'number') {
      dispatch({ type: 'overlay' });
      return;
    }

    const minLeftOffset = (windowWidth - postWidth) / 2 - 40;
    const minSpacing = 24;
    const diff = (windowWidth - postWidth) / 2 - minSpacing - TOC_WIDTH;
    if (diff > 0) {
      dispatch({ type: 'offset', transition: 0 });
      return;
    }
    if (diff > -minLeftOffset) {
      dispatch({ type: 'offset', transition: diff });
      return;
    }

    dispatch({ type: 'overlay' });
  }, [quill]);

  const setIsShowingTOC = (newIsShowingTOC: boolean) => {
    originalSetIsShowingTOC(newIsShowingTOC);

    if (newIsShowingTOC) {
      calculateLeftOffset();
    } else {
      dispatch({ type: 'reset' });
    }
  };

  useEffect(() => {
    if (!isShowingTOC) return undefined;

    const listener = _.debounce(calculateLeftOffset, 200);
    window.addEventListener('resize', listener);
    return () => {
      window.removeEventListener('resize', listener);
      listener.cancel();
    };
  }, [isShowingTOC, calculateLeftOffset]);

  if (error) {
    return <EmbeddedNotFoundView />;
  }

  return (
    <IsPublicViewProvider publicView={!isLoggedIn}>
      <div className={styles.container} {...anchorScrollingHandlerProps}>
        <PageMeta title={`${title || _.startCase(name)} - Slab`} />
        <div className={styles.content}>
          {content ? (
            <div className={styles.postContainer}>
              <div
                className={styles.fullWidthContainer}
                style={{
                  translate: state.leftTransition,
                }}
              >
                <AssetTokenProvider entityAssetToken={entityAssetToken}>
                  <QuillComponent
                    namespace="embedded"
                    content={content.delta}
                    containerClassName={styles.editorContainer}
                    rootClassName={styles.editorRoot}
                    readOnly
                  />
                </AssetTokenProvider>
              </div>
              <AnimatePresence>
                {state.isOverlayMode && (
                  <OpacityMotionTransition
                    className={styles.overlay}
                    transition={{
                      type: TOC_TRANSITION_TIMING_FUNCTION,
                      duration: TOC_TRANSITION_DURATION / 1000,
                    }}
                    onClick={() => setIsShowingTOC(false)}
                  />
                )}
              </AnimatePresence>
            </div>
          ) : (
            <div className={styles.postContainer}>
              <div className={styles.editorContainer}>
                <PostSkeleton />
              </div>
            </div>
          )}
          <div className={styles.tocContainer}>
            <ConnectedTableOfContents postId={postId}>
              {(tocProps) =>
                isShowingTOC && (
                  <div className={styles.tocContent}>
                    <h4 className={styles.tocTitle}>Table of Contents</h4>
                    <TableOfContents {...tocProps} />
                  </div>
                )
              }
            </ConnectedTableOfContents>
          </div>
        </div>
        {post && (
          <div className={styles.footer}>
            <Link className={styles.logoAndTitle} to={postPath(post)} newTab>
              <SlabLogo className={styles.logo} />
              <span className={styles.title}>{title}</span>
            </Link>
            <Button
              size="micro"
              className={classNames(styles.tocButton, {
                [styles.isActive]: isShowingTOC,
              })}
              onClick={() => {
                setIsShowingTOC(!isShowingTOC);
              }}
            >
              <SVGIcon>{tocIcon}</SVGIcon>
            </Button>
          </div>
        )}
      </div>
    </IsPublicViewProvider>
  );
};

export default withQuillContextProvider(EmbeddedPostView);
